import CodeEditor, { CodeEditorRef } from "../../components/canvas/CodeEditor";
import { EditorLoadingStage, EditorViewMode } from "../../stores/UIStore";
import { PyodideState, pyodideService } from "../../services/PyodideService";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAuth, useOrganization, useUser } from "@clerk/clerk-react";
import { useNavigate, useParams } from "@tanstack/react-router";

import AgentPanel from "../../components/agent/AgentPanel";
import LoadingState from "../../components/ui/loading-state";
import LogsPanel from "../../components/console/LogsPanel";
import SecondaryTopBar from "../../components/nav/SecondaryTopBar";
import { SidebarTab } from "../../../types";
import TopBar from "../../components/nav/CanvasTopBar";
import WebPreview from "../../components/canvas/WebPreview";
import { cn } from "../../lib/utils";
import { filesystemService } from "../../utils/filesystem";
import { observer } from "mobx-react-lite";
import { projectService } from "../../services/ProjectService";
import { rootStore } from "../../stores/RootStore";
import { toast } from "sonner";
import { useKeyboardShortcuts } from "../../hooks/useKeyboardShortcuts";
import useProjectManagement from "../../hooks/useProjectManagement";

const EditorPage: React.FC = observer(() => {
    const params = useParams({ from: "/org/$orgSlug/$projectId" });
    const navigate = useNavigate();
    const { projectStore, uiStore } = rootStore;
    const codeEditorRef = useRef<CodeEditorRef>(null);
    const orgSlug = params.orgSlug as string;
    const { user } = useUser();
    const { organization } = useOrganization();
    const { getToken } = useAuth();
    const [isDeploying, setIsDeploying] = useState(false);
    const [isExporting, setIsExporting] = useState(false);
    const [canUndo, setCanUndo] = useState(false);
    const [canRedo, setCanRedo] = useState(false);

    const { saveProjectToCloud, isSavingToCloud } = useProjectManagement(
        uiStore.updateLogs.bind(uiStore),
        (tab: SidebarTab) => uiStore.setActiveTab(tab)
    );

    // Listen for Pyodide loading status
    useEffect(() => {
        const unsubscribe = pyodideService.subscribeToWorkerMessage(
            "LOADING_STATUS",
            (data) => {
                uiStore.setPyodideLoadingStatus(data.status);
            }
        );

        return () => unsubscribe();
    }, [uiStore]);

    // Step 1: Load project data
    useEffect(() => {
        if (!params.projectId) return;

        const loadProjectData = async () => {
            try {
                uiStore.setLoadingStage(EditorLoadingStage.LOADING_PROJECT);

                // First check if we already have this project loaded
                if (projectStore.projectId === params.projectId) {
                    uiStore.setLoadingStage(EditorLoadingStage.LOADING_FILESYSTEM);
                    return;
                }

                uiStore.updateLogs(`Loading project ${params.projectId}...`);
                await projectStore.loadProject(params.projectId, uiStore.updateLogs.bind(uiStore));

                if (!projectStore.selectedFile) {
                    projectStore.setSelectedFile("/project/hello.py");
                }

                uiStore.setLoadingStage(EditorLoadingStage.LOADING_FILESYSTEM);
            } catch (error) {
                uiStore.updateLogs(`Error loading project: ${error instanceof Error ? error.message : String(error)}`);
                navigate({ to: "/org/$orgSlug", params: { orgSlug } });
            }
        };

        loadProjectData();
    }, [params.projectId, orgSlug, navigate, projectStore, uiStore]);

    // Step 2: Load filesystem from IndexedDB
    useEffect(() => {
        if (uiStore.loadingStage !== EditorLoadingStage.LOADING_FILESYSTEM || !params.projectId) return;

        const loadFilesystem = async () => {
            try {
                // Load filesystem from IndexedDB
                const fsLoaded = await filesystemService.loadFromIndexedDB(params.projectId);

                if (fsLoaded) {
                    uiStore.updateLogs("Filesystem loaded successfully");
                    uiStore.setFilesystemReady(true);

                    // Set default file after filesystem is loaded
                    projectStore.setSelectedFile("/project/hello.py");

                    // Move to RENDERING_UI stage
                    uiStore.setLoadingStage(EditorLoadingStage.RENDERING_UI);
                } else {
                    // If filesystem couldn't be loaded, create project files first (without waiting for Pyodide)
                    uiStore.updateLogs("No saved files found, creating project template files...");
                    try {
                        await pyodideService.createNewProject(); // will stash files in IndexedDB / pendingFiles
                        // Persist to IndexedDB explicitly (in case createNewProject ran before Pyodide ready)
                        await filesystemService.saveToIndexedDB();

                        uiStore.updateLogs("Template files created in storage");
                        uiStore.setFilesystemReady(true);

                        // Set default file immediately
                        projectStore.setSelectedFile("/project/hello.py");

                        // Move directly to rendering UI
                        uiStore.setLoadingStage(EditorLoadingStage.RENDERING_UI);
                    } catch (e) {
                        uiStore.updateLogs(`Error creating template files: ${e instanceof Error ? e.message : e}`);
                        // Fall back to initializing Pyodide if template creation fails
                        uiStore.setLoadingStage(EditorLoadingStage.INITIALIZING_PYODIDE);
                        uiStore.setFilesystemReady(false);
                    }
                }
            } catch (error) {
                uiStore.updateLogs(`Error loading filesystem: ${error instanceof Error ? error.message : String(error)}`);
                uiStore.setLoadingStage(EditorLoadingStage.INITIALIZING_PYODIDE);
                uiStore.setFilesystemReady(false);
            }
        };

        loadFilesystem();
    }, [uiStore.loadingStage, params.projectId, uiStore, projectStore]);

    // Step 3: Initialize Pyodide when needed (non-blocking)
    useEffect(() => {
        // When filesystem is ready but Pyodide isn't yet initialized or when explicit initialization is needed
        if (
            (uiStore.filesystemReady && !pyodideService.isReady()) ||
            uiStore.loadingStage === EditorLoadingStage.INITIALIZING_PYODIDE
        ) {
            // Always set selected file immediately if filesystem is ready
            // This doesn't need to wait for Pyodide
            if (uiStore.filesystemReady && !projectStore.selectedFile) {
                projectStore.setSelectedFile("/project/hello.py");

                // Proceed to RENDERING_UI stage regardless of Pyodide status
                if (uiStore.loadingStage === EditorLoadingStage.LOADING_FILESYSTEM) {
                    uiStore.setLoadingStage(EditorLoadingStage.RENDERING_UI);
                }
            }

            const initPyodide = async () => {
                try {
                    if (!pyodideService.isReady()) {
                        await uiStore.initializePyodide();
                    }

                    // If we need to create a new filesystem because none was loaded
                    if (!uiStore.filesystemReady) {
                        await pyodideService.createNewProject();
                        uiStore.updateLogs("Created new filesystem");
                        uiStore.setFilesystemReady(true);

                        // Set default file when creating a new filesystem
                        projectStore.setSelectedFile("/project/hello.py");

                        // Once filesystem is ready, we can move to RENDERING_UI
                        uiStore.setLoadingStage(EditorLoadingStage.RENDERING_UI);
                    }

                    window.dispatchEvent(new CustomEvent('filesystem:ready'));
                } catch (error) {
                    uiStore.updateLogs(`Error initializing environment: ${error instanceof Error ? error.message : String(error)}`);
                    console.error("Failed to initialize Pyodide:", error);
                }
            };

            // Start Pyodide initialization in background
            initPyodide();
        }

        // If filesystem is ready, always set selected file and proceed to UI rendering
        // This ensures we don't wait for Pyodide to set files or render UI
        if (uiStore.filesystemReady) {
            if (!projectStore.selectedFile) {
                projectStore.setSelectedFile("/project/hello.py");
            }

            if (uiStore.loadingStage === EditorLoadingStage.LOADING_FILESYSTEM) {
                uiStore.setLoadingStage(EditorLoadingStage.RENDERING_UI);
            }
        }
    }, [uiStore.loadingStage, uiStore.filesystemReady, uiStore, projectStore]);

    // Reset UI state if project ID is not valid
    useEffect(() => {
        if (!params.projectId && uiStore.agentVisible) {
            uiStore.setAgentVisible(false);
        }
        if (!params.projectId && uiStore.logsVisible) {
            uiStore.setLogsVisible(false);
        }
    }, [params.projectId, uiStore.agentVisible, uiStore.logsVisible, uiStore]);

    const handleRefresh = useCallback(async () => {
        try {
            // Stop the current script
            await pyodideService.stopScript();
            // Run the script again
            const scriptToRun = projectStore.selectedFile || `/project/hello.py`;
            const result = await pyodideService.startScript(scriptToRun);

            if (!result.success) {
                throw new Error(`Failed to run script: ${result.error}`);
            }

            // Send components to iframe
            if (result.components) {
                const iframe = document.querySelector('.preview-iframe') as HTMLIFrameElement;
                if (iframe?.contentWindow) {
                    iframe.contentWindow.postMessage({
                        type: 'components',
                        components: result.components
                    }, '*');
                }
            }
        } catch (err) {
            console.error('Error recomputing:', err);
        }
    }, [projectStore.selectedFile]);

    // Listen for filesystem:file-changed events to refresh editor
    useEffect(() => {
        const handleFileChanged = (event: CustomEvent<{ path: string, source?: string }>) => {
            if (event.detail.source === 'editor-save') {
                return;
            }

            // Check if the changed file is the one we're interested in
            if (event.detail.path === '/project/hello.py' &&
                projectStore.selectedFile === '/project/hello.py') {
                // Refresh the editor to show updated content
                if (codeEditorRef.current) {
                    codeEditorRef.current.refresh();
                }

                // Automatically trigger a refresh of the preview
                handleRefresh();
            }
        };

        // Add event listener
        window.addEventListener('filesystem:file-changed', handleFileChanged as EventListener);

        return () => {
            // Remove event listener on cleanup
            window.removeEventListener('filesystem:file-changed', handleFileChanged as EventListener);
        };
    }, [projectStore.selectedFile, codeEditorRef, handleRefresh]);

    // Set up keyboard shortcuts
    useKeyboardShortcuts({
        projectId: projectStore.projectId,
        handleToggleSidebar: uiStore.handleToggleSidebar.bind(uiStore),
        setAgentVisible: uiStore.setAgentVisible.bind(uiStore),
        setLogsVisible: uiStore.setLogsVisible.bind(uiStore),
        agentVisible: uiStore.agentVisible,
        logsVisible: uiStore.logsVisible,
    });

    // Memoize callback functions
    const handleCreateNewProject = useCallback(async () => {
        try {
            const result = await projectStore.createNewProject(
                uiStore.updateLogs.bind(uiStore),
                uiStore.setActiveTab.bind(uiStore)
            );

            if (result && result.projectId) {
                navigate({ to: "/org/$orgSlug/$projectId", params: { orgSlug, projectId: result.projectId } });
            }
        } catch (error) {
            console.error("Failed to create project:", error);
        }
    }, [projectStore, uiStore, navigate, orgSlug]);

    const handleReturnToProjects = useCallback(() => {
        projectStore.returnToProjects();
        navigate({ to: "/org/$orgSlug", params: { orgSlug } });
    }, [projectStore, navigate, orgSlug]);

    const saveProjectToDisk = useCallback(async () => {
        try {
            await filesystemService.downloadProjectAsZip();
        } catch (e: unknown) {
            if (e instanceof Error) {
                console.error('Download error:', e);
            } else {
            }
        }
    }, [uiStore]);

    const handleSetSelectedFile = useCallback((file: string | null) => {
        projectStore.setSelectedFile(file);
    }, [projectStore]);

    const handleCloseLogs = useCallback(() => {
        uiStore.setLogsVisible(false);
    }, [uiStore]);

    const handleSetActiveTab = useCallback((tab: SidebarTab) => {
        uiStore.setActiveTab(tab);
    }, [uiStore]);

    const handleDeploy = useCallback(async () => {
        if (!projectStore.projectId || !user || !organization) return;

        try {
            setIsDeploying(true);
            uiStore.updateLogs("Starting HTML export...");

            const token = await getToken();
            if (!token) {
                throw new Error("Authentication token not available");
            }

            projectService.setToken(token);
            projectService.setUserContext(user.id, organization.id);
            projectService.setCurrentProjectId(projectStore.projectId);

            // First, export the HTML files using preswald
            const exportResult = await pyodideService.exportHtml();
            if (!exportResult.success || !exportResult.files) {
                throw new Error(`HTML export failed: ${exportResult.error || 'Unknown error'}`);
            }

            // Debug: Log detailed information about exported files
            const exportedFiles = exportResult.files;
            const fileCount = Object.keys(exportedFiles).length;
            console.log(`[DEBUG] HTML export completed. Total files: ${fileCount}`);
            console.log(`[DEBUG] Exported files:`, Object.keys(exportedFiles));

            // Log file details
            Object.entries(exportedFiles).forEach(([filePath, fileData]) => {
                console.log(`[DEBUG] File: ${filePath}, Size: ${fileData.length} bytes, Type: ${fileData.constructor.name}`);
            });

            uiStore.updateLogs(`HTML export completed successfully. ${fileCount} files exported: ${Object.keys(exportedFiles).join(', ')}`);
            uiStore.updateLogs("Uploading files to cloud storage...");

            const formData = new FormData();
            formData.append('project_id', projectStore.projectId);

            let filesAddedToForm = 0;
            // Add each exported file to the form data
            for (const [filePath, fileData] of Object.entries(exportedFiles)) {
                try {
                    // Convert Uint8Array to Blob - ensure proper type conversion
                    const uint8Array = new Uint8Array(fileData);
                    const blob = new Blob([uint8Array], {
                        type: getContentType(filePath)
                    });

                    console.log(`[DEBUG] Adding file to FormData: ${filePath}, Blob size: ${blob.size}, Content-Type: ${getContentType(filePath)}`);
                    // Use a unique key for each file to avoid overwriting
                    formData.append(`file_${filesAddedToForm}`, blob, filePath);
                    filesAddedToForm++;
                } catch (fileError) {
                    console.error(`[DEBUG] Error processing file ${filePath}:`, fileError);
                    uiStore.updateLogs(`Warning: Failed to process file ${filePath}: ${fileError}`);
                }
            }

            console.log(`[DEBUG] Total files added to FormData: ${filesAddedToForm} out of ${fileCount} exported files`);

            // Calculate total size
            const totalSize = Object.values(exportedFiles).reduce((sum, fileData) => sum + fileData.length, 0);
            console.log(`[DEBUG] Total upload size: ${(totalSize / 1024 / 1024).toFixed(2)} MB`);

            uiStore.updateLogs(`Prepared ${filesAddedToForm} files for upload (${(totalSize / 1024 / 1024).toFixed(2)} MB)...`);

            // Debug: Log FormData contents
            console.log(`[DEBUG] FormData entries:`);
            for (const [key, value] of formData.entries()) {
                if (value instanceof File) {
                    console.log(`[DEBUG] FormData key: ${key}, File: ${value.name}, Size: ${value.size}, Type: ${value.type}`);
                } else {
                    console.log(`[DEBUG] FormData key: ${key}, Value: ${value}`);
                }
            }

            const response = await fetch(`${import.meta.env.VITE_BACKEND_URL}/deploy`, {
                method: 'POST',
                headers: {
                    'X-Organization-ID': organization.id,
                    'X-User-ID': user.id,
                    'Authorization': `Bearer ${token}`,
                },
                body: formData,
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({ error: response.statusText }));
                throw new Error(`Upload failed: ${errorData.error || response.statusText}`);
            }

            const result = await response.json();
            console.log(`[DEBUG] Backend response:`, result);

            if (result.success) {
                uiStore.updateLogs(`Deployment successful! Your app is available at: ${result.website_url}`);
                uiStore.updateLogs(`Backend processed ${result.files_uploaded} files.`);

                // Compare counts
                if (result.files_uploaded !== fileCount) {
                    console.warn(`[DEBUG] File count mismatch! Exported: ${fileCount}, Uploaded: ${result.files_uploaded}`);
                    uiStore.updateLogs(`Warning: File count mismatch. Exported ${fileCount} files but backend processed ${result.files_uploaded} files.`);
                }

                try {
                    if (result.project || result.deployment || result.website_url) {
                        const deploymentData = await projectService.processDeploymentResult(result);
                    } else {
                        const updatedProject = await projectService.updateProject(projectStore.projectId, {
                            isPublished: true,
                            status: 'published',
                            deploymentUrl: result.website_url,
                            updatedAt: Date.now()
                        });
                    }
                } catch (updateError) {
                    console.error(`[DEBUG] Failed to update project state in IndexedDB:`, updateError);
                }

                if (result.website_url) {
                    toast.success(`Deployment successful! Your app is available at: ${result.website_url}`);
                }
            } else {
                throw new Error(result.error || 'Upload failed');
            }

        } catch (error) {
            console.error(`[DEBUG] Deployment error:`, error);
            uiStore.updateLogs(`Deployment failed: ${error instanceof Error ? error.message : String(error)}`);
            console.error("Deployment error:", error);
        } finally {
            setIsDeploying(false);
        }
    }, [projectStore.projectId, user, organization, getToken, uiStore]);

    const handleSaveToCloud = useCallback(async () => {
        if (!projectStore.projectId || !user || !organization) return;

        projectService.setUserContext(user.id, organization.id);
        projectService.setCurrentProjectId(projectStore.projectId);

        const result = await saveProjectToCloud();

        if (result?.success) {
            toast.success(`Successfully saved ${result.savedCount} project files to cloud storage!`);
        } else {
            toast.error(`Failed to save files to cloud: ${result?.error || 'Unknown error'}`);
        }
    }, [projectStore.projectId, user, organization, saveProjectToCloud]);

    // Helper function to determine content type
    const getContentType = (filePath: string): string => {
        if (filePath.endsWith('.html')) return 'text/html';
        if (filePath.endsWith('.css')) return 'text/css';
        if (filePath.endsWith('.js')) return 'application/javascript';
        if (filePath.endsWith('.json')) return 'application/json';
        if (filePath.endsWith('.png')) return 'image/png';
        if (filePath.endsWith('.jpg') || filePath.endsWith('.jpeg')) return 'image/jpeg';
        if (filePath.endsWith('.svg')) return 'image/svg+xml';
        return 'application/octet-stream';
    };

    const handleExportHtml = useCallback(async () => {
        if (!projectStore.projectId) return;

        try {
            setIsExporting(true);
            uiStore.updateLogs("Starting HTML export...");

            // Export the HTML files using preswald
            const exportResult = await pyodideService.exportHtml();
            if (!exportResult.success || !exportResult.files) {
                throw new Error(`HTML export failed: ${exportResult.error || 'Unknown error'}`);
            }

            uiStore.updateLogs(`HTML export completed successfully. ${Object.keys(exportResult.files).length} files exported.`);

        } catch (error) {
            uiStore.updateLogs(`HTML export failed: ${error instanceof Error ? error.message : String(error)}`);
            console.error("HTML export error:", error);
        } finally {
            setIsExporting(false);
        }
    }, [projectStore.projectId, uiStore]);

    useEffect(() => {
        if (params.projectId) {
            uiStore.startVersionChecking();
        }

        return () => {
            uiStore.stopVersionChecking();
        };
    }, [params.projectId, uiStore]);

    useEffect(() => {
        const updateUndoRedoState = () => {
            if (projectStore.selectedFile === "/project/hello.py") {
                setCanUndo(uiStore.canUndo());
                setCanRedo(uiStore.canRedo());
            } else {
                setCanUndo(codeEditorRef.current?.canUndo() || false);
                setCanRedo(codeEditorRef.current?.canRedo() || false);
            }
        };

        updateUndoRedoState();

        const interval = setInterval(updateUndoRedoState, 100);
        return () => clearInterval(interval);
    }, [projectStore.selectedFile, uiStore.versions, uiStore.currentVersionIndex]);

    useEffect(() => {
        const handleGlobalUndo = async (e: KeyboardEvent) => {
            if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) {
                e.preventDefault();
                if (projectStore.selectedFile === "/project/hello.py") {
                    const content = uiStore.undo();
                    if (content !== null && codeEditorRef.current) {
                        codeEditorRef.current.updateFromVersion(content);
                    }
                } else if (codeEditorRef.current) {
                    codeEditorRef.current.undo();
                }
            }
        };

        const handleGlobalRedo = async (e: KeyboardEvent) => {
            if ((e.ctrlKey || e.metaKey) && (e.key === 'y' || (e.key === 'z' && e.shiftKey))) {
                e.preventDefault();
                if (projectStore.selectedFile === "/project/hello.py") {
                    const content = uiStore.redo();
                    if (content !== null && codeEditorRef.current) {
                        codeEditorRef.current.updateFromVersion(content);
                    }
                } else if (codeEditorRef.current) {
                    codeEditorRef.current.redo();
                }
            }
        };

        const handleGlobalUndoWrapper = (e: KeyboardEvent) => {
            handleGlobalUndo(e).catch(console.error);
        };

        const handleGlobalRedoWrapper = (e: KeyboardEvent) => {
            handleGlobalRedo(e).catch(console.error);
        };

        window.addEventListener('keydown', handleGlobalUndoWrapper);
        window.addEventListener('keydown', handleGlobalRedoWrapper);

        return () => {
            window.removeEventListener('keydown', handleGlobalUndoWrapper);
            window.removeEventListener('keydown', handleGlobalRedoWrapper);
        };
    }, [projectStore.selectedFile, uiStore]);

    const handleUndoFromButton = useCallback(() => {
        if (projectStore.selectedFile === "/project/hello.py") {
            const content = uiStore.undo();
            if (content !== null && codeEditorRef.current) {
                codeEditorRef.current.updateFromVersion(content);
            }
        } else if (codeEditorRef.current) {
            codeEditorRef.current.undo();
        }
    }, [projectStore.selectedFile, uiStore]);

    const handleRedoFromButton = useCallback(() => {
        if (projectStore.selectedFile === "/project/hello.py") {
            const content = uiStore.redo();
            if (content !== null && codeEditorRef.current) {
                codeEditorRef.current.updateFromVersion(content);
            }
        } else if (codeEditorRef.current) {
            codeEditorRef.current.redo();
        }
    }, [projectStore.selectedFile, uiStore]);

    // Memoize components to prevent unnecessary rerenders
    const topBar = useMemo(() => (
        <TopBar
            logsVisible={uiStore.logsVisible}
            agentVisible={uiStore.agentVisible}
            onNewProject={handleCreateNewProject}
            onOpenProject={handleReturnToProjects}
            setSelectedFile={handleSetSelectedFile}
            activeTab={uiStore.activeTab}
            setActiveTab={handleSetActiveTab}
            projectId={projectStore.projectId}
            isPyodideLoaded={pyodideService.hasWorker()}
            pyodideState={pyodideService.isReady() ? PyodideState.READY : PyodideState.LOADING}
            pyodideLoadingStatus={uiStore.pyodideLoadingStatus}
        />
    ), [
        uiStore.logsVisible,
        uiStore.agentVisible,
        uiStore.activeTab,
        uiStore.pyodideLoadingStatus,
        projectStore.projectId,
        handleCreateNewProject,
        handleReturnToProjects,
        handleSetSelectedFile,
        handleSetActiveTab
    ]);

    const secondaryTopBar = useMemo(() => (
        <SecondaryTopBar
            onSaveProject={saveProjectToDisk}
            onDeploy={handleDeploy}
            onSaveToCloud={handleSaveToCloud}
            isDeploying={isDeploying}
            isSavingToCloud={isSavingToCloud}
            projectId={projectStore.projectId}
            isPyodideLoaded={pyodideService.hasWorker()}
            pyodideState={pyodideService.isReady() ? PyodideState.READY : PyodideState.LOADING}
            pyodideLoadingStatus={uiStore.pyodideLoadingStatus}
            viewMode={uiStore.viewMode}
            onViewModeChange={uiStore.setViewMode.bind(uiStore)}
            onUndo={handleUndoFromButton}
            onRedo={handleRedoFromButton}
            canUndo={canUndo}
            canRedo={canRedo}
        />
    ), [
        saveProjectToDisk,
        handleDeploy,
        handleSaveToCloud,
        handleExportHtml,
        isDeploying,
        isSavingToCloud,
        isExporting,
        projectStore.projectId,
        uiStore.pyodideLoadingStatus,
        uiStore.viewMode,
        handleUndoFromButton,
        handleRedoFromButton,
        canUndo,
        canRedo
    ]);

    const mainContent = useMemo(() => {
        return (
            <div className="flex flex-1 h-full">
                {(uiStore.viewMode === EditorViewMode.CODE || uiStore.viewMode === EditorViewMode.SPLIT) && (
                    <div className={cn(
                        "min-w-0 overflow-hidden",
                        uiStore.viewMode === EditorViewMode.SPLIT ? "flex-1" : "flex-1"
                    )}>
                        <CodeEditor
                            ref={codeEditorRef}
                            selectedFile={projectStore.selectedFile!}
                            appLoading={projectStore.loading}
                            isGenerating={projectStore.isGenerating}
                            rootFolder={projectStore.projectName}
                            isPyodideLoaded={pyodideService.hasWorker()}
                            setSelectedFile={handleSetSelectedFile}
                            showSidebar={uiStore.viewMode !== EditorViewMode.SPLIT}
                        />
                    </div>
                )}
                {(uiStore.viewMode === EditorViewMode.PREVIEW || uiStore.viewMode === EditorViewMode.SPLIT) && (
                    <div className={cn(
                        "min-w-0 overflow-hidden",
                        uiStore.viewMode === EditorViewMode.SPLIT ? "flex-1" : "flex-1"
                    )}>
                        <WebPreview scriptPath="/project/hello.py" />
                    </div>
                )}
            </div>
        );
    }, [projectStore.selectedFile, projectStore.loading, projectStore.isGenerating, projectStore.projectName, uiStore.viewMode]);

    const agentPanel = useMemo(() => {
        if (uiStore.agentVisible && projectStore.projectId) {
            return (
                <AgentPanel
                    className="agent-panel fixed-width"
                    selectedFile={projectStore.selectedFile!}
                    codeEditorRef={codeEditorRef}
                />
            );
        }
        return null;
    }, [uiStore.agentVisible, projectStore.projectId, projectStore.selectedFile, codeEditorRef]);

    const logsPanel = useMemo(() => (
        <LogsPanel
            logs={uiStore.logs}
            isVisible={uiStore.logsVisible}
            onClose={handleCloseLogs}
            agentVisible={uiStore.agentVisible}
        />
    ), [uiStore.logs, uiStore.logsVisible, handleCloseLogs, uiStore.agentVisible]);

    // Only show loading screen when filesystem is not ready
    if (uiStore.shouldShowLoadingScreen) {
        return (
            <div className="flex items-center justify-center h-screen">
                <LoadingState size="lg" message={uiStore.getLoadingMessage()} />
            </div>
        );
    }

    return (
        <div className="flex flex-col h-screen">
            <div className="flex flex-1 h-full overflow-hidden">
                <main className="appcontainer-main flex flex-col flex-1 relative">
                    {topBar}
                    {secondaryTopBar}
                    <div className="flex flex-1 min-h-0">
                        <div className="flex-1 min-w-0 overflow-hidden">
                            {mainContent}
                        </div>
                        {agentPanel}
                    </div>
                    <div className="absolute bottom-0 left-0 right-0">
                        {logsPanel}
                    </div>
                </main>
            </div>
        </div>
    );
});

export default React.memo(EditorPage);
